/**
 * src/shell/Sidebar.tsx
 *
 * Categorized, Accessible Collapsible Navigation Sidebar with Versioned State Persistence,
 * Off-Canvas Mobile Drawer, Debounced Search, and Multi-Tier Alert Bubbling.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  BroadcastIcon,
  UsersIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderClosedIcon,
  FolderOpenIcon,
  SpeechBalloonIcon,
  RunningSprintIcon,
  BookKnowledgeIcon,
  GlobeMeridiansIcon,
  FileTextIcon,
  ShoppingBagsGlassIcon,
  PosTerminalIcon,
  DesktopKioskIcon,
  PackageBoxIcon,
  CrmIcon,
  InvoiceStudioIcon,
  FinanceLedgerIcon,
  ShieldGovernanceIcon,
  SparklesIcon,
  CpuIcon,
  ActivityIcon,
  GalleryIcon,
} from './icons';
import { SidebarUtilityDock } from './SidebarUtilityDock';
import { useTranslation } from '../i18n/useTranslation.js';
import { isPluginIdEnabled } from './channels/channel-plugin-registry.js';
import { usePluginExtensionSlots } from './hooks/usePluginExtensionSlots.js';
import { ProfileConfig } from '../config/profiles/types';
import { isChannelEnabledForProfile } from './config/channel-module-map';
import {
  channelAssignmentStore,
  DEFAULT_CLEAN_CATEGORIES,
} from './channels/channel-assignment-store.js';
import { SovereignModuleSlotRenderer } from './modules/SovereignModuleSlotRenderer.js';
import { PrivateRoomInviteModal } from './channels/PrivateRoomInviteModal.js';

export interface ChannelCategoryDef {
  id: string;
  name: string;
  description?: string;
  icon: React.FC<any>;
  defaultOpen: boolean;
  channelIds: string[];
}

export const SIDEBAR_STATE_KEY = 'mesh:sidebar:categories';
export const SIDEBAR_STATE_VERSION = 2;

export const LEGACY_ENTERPRISE_CATEGORIES: ChannelCategoryDef[] = [];

export const CATEGORIES_CONFIG: ChannelCategoryDef[] = DEFAULT_CLEAN_CATEGORIES;

interface SidebarProps {
  currentRoom: string;
  onSelectRoom: (room: string) => void;
  peers: Array<{ nodeId: string; addressUri: string; status?: 'online' | 'offline' }>;
  broadcasts: Array<{ uri: string; topic: string }>;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  enabledPlugins?: Record<string, boolean>;
  activeJurisdiction?: string;
  isOverlay?: boolean;
  onClose?: () => void;
  onSelectBroadcast?: (broadcast: any) => void;
  onSelectPeer?: (peerId: string) => void;
  onOpenChannelModal?: () => void;
  onOpenVoiceOverlay?: () => void;
  onToggleNotifications?: () => void;
  unreadNotifications?: number;
  profile?: ProfileConfig;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentRoom,
  onSelectRoom,
  peers,
  broadcasts,
  isCollapsed,
  onToggleCollapse,
  enabledPlugins = {},
  activeJurisdiction = 'GLOBAL',
  isOverlay = false,
  onClose,
  onSelectBroadcast,
  onSelectPeer,
  onOpenChannelModal,
  onOpenVoiceOverlay,
  onToggleNotifications,
  unreadNotifications = 0,
  profile,
}) => {
  const { t } = useTranslation();
  const dynamicSlots = usePluginExtensionSlots();

  const [isBroadcastsOpen, setIsBroadcastsOpen] = useState(true);
  const [isUsersOpen, setIsUsersOpen] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteRoomId, setInviteRoomId] = useState('private');
  const [pendingKnocks, setPendingKnocks] = useState<Array<{ roomId: string; knockId: string }>>([]);
  const [privateParticipants, setPrivateParticipants] = useState<Array<{ id: string; name: string; role: string; email?: string; phone?: string }>>([]);

  useEffect(() => {
    let active = true;
    const fetchPrivateParticipants = async () => {
      try {
        const res = await fetch('/api/v1/invites/rooms/private/participants');
        if (res.ok) {
          const data = await res.json();
          if (active && Array.isArray(data.participants)) {
            setPrivateParticipants(data.participants);
          }
        }
      } catch {}
    };
    fetchPrivateParticipants();
    const interval = setInterval(fetchPrivateParticipants, 2000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const handleKickParticipant = async (targetSessionId: string) => {
    try {
      await fetch('/api/v1/invites/rooms/private/evict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetSessionId, reason: 'Removed by host from sidebar directory' }),
      });
      setPrivateParticipants((prev) => prev.filter((p) => p.id !== targetSessionId));
    } catch {}
  };

  useEffect(() => {
    const handler = (e: any) => {
      const list = e.detail || [];
      setPendingKnocks(list.map((k: any) => ({ roomId: k.roomId || "private", knockId: k.knockId })));
    };
    window.addEventListener("knocks:updated", handler);
    return () => window.removeEventListener("knocks:updated", handler);
  }, []);

  // 150ms Debounced search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchFilter), 150);
    return () => clearTimeout(handler);
  }, [searchFilter]);

  const isLegacyDesktop = profile?.id === 'mesh-node';
  const baseCategories = isLegacyDesktop ? LEGACY_ENTERPRISE_CATEGORIES : CATEGORIES_CONFIG;

  const [categories, setCategories] = useState<ChannelCategoryDef[]>(() => {
    return isLegacyDesktop ? LEGACY_ENTERPRISE_CATEGORIES : channelAssignmentStore.getCategories();
  });

  useEffect(() => {
    if (isLegacyDesktop) return;
    const onStoreChange = (newCats: ChannelCategoryDef[]) => {
      setCategories(newCats);
    };
    channelAssignmentStore.on('change', onStoreChange);
    return () => {
      channelAssignmentStore.off('change', onStoreChange);
    };
  }, [isLegacyDesktop]);

  const activeCategories = useMemo(() => {
    const cats = [...categories];
    if (enabledPlugins?.CommercePlugin === true && !cats.some((c) => c.channelIds.includes('commerce'))) {
      cats.push({
        id: 'ecommerce',
        name: 'Ecommerce & Storefront',
        icon: ShoppingBagsGlassIcon,
        defaultOpen: true,
        channelIds: ['commerce', 'pos', 'kiosk', 'inventory'],
      });
    }
    return cats;
  }, [categories, enabledPlugins]);

  // Versioned state loader
  const [categoryOpenStates, setCategoryOpenStates] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_STATE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.version === SIDEBAR_STATE_VERSION && parsed.collapsed) {
          const res: Record<string, boolean> = {};
          baseCategories.forEach((cat) => {
            res[cat.id] = parsed.collapsed[cat.id] !== undefined ? !parsed.collapsed[cat.id] : cat.defaultOpen;
          });
          return res;
        }
      }
    } catch {}
    const initial: Record<string, boolean> = {};
    baseCategories.forEach((cat) => {
      initial[cat.id] = cat.defaultOpen;
    });
    return initial;
  });

  const toggleCategory = (catId: string) => {
    setCategoryOpenStates((prev) => {
      const next = { ...prev, [catId]: !prev[catId] };
      try {
        const collapsedMap: Record<string, boolean> = {};
        Object.entries(next).forEach(([k, v]) => {
          collapsedMap[k] = !v;
        });
        localStorage.setItem(
          SIDEBAR_STATE_KEY,
          JSON.stringify({ version: SIDEBAR_STATE_VERSION, collapsed: collapsedMap, timestamp: new Date().toISOString() })
        );
      } catch {}
      return next;
    });
  };

  // Auto-expand category containing active channel
  useEffect(() => {
    const parent = baseCategories.find((cat) => cat.channelIds.includes(currentRoom));
    if (parent && !categoryOpenStates[parent.id]) {
      setCategoryOpenStates((prev) => ({ ...prev, [parent.id]: true }));
    }
  }, [currentRoom, baseCategories]);

  const getChannelLabel = (key: string, fallback: string) => {
    const val = t(key);
    return !val || val === key ? fallback : val;
  };

  const isEfrisOrMyInvois = activeJurisdiction === 'UG' || activeJurisdiction === 'MY';

  const channelMap: Record<string, { id: string; name: string; tooltip?: string; icon: React.FC<any>; pluginKey?: string; requiresFiscalJurisdiction?: boolean }> = {
    general: { id: 'general', name: 'General', tooltip: 'Open decentralized team room', icon: SpeechBalloonIcon },
    private: { id: 'private', name: 'Private', tooltip: 'Guarded access channel with host admission gate', icon: ShieldGovernanceIcon },
  };

  const isChannelVisible = (ch: typeof channelMap[string]) => {
    if ((profile as unknown as { role?: string })?.role === 'guest' && ch.id !== 'private') return false;
    if (profile?.enabledModules) {
      const enabledIds = profile.enabledModules.map((m) => m.id);
      if (!isChannelEnabledForProfile(ch.id, enabledIds)) return false;
    }
    if (ch.pluginKey && !isPluginIdEnabled(enabledPlugins, ch.pluginKey)) return false;
    if (ch.requiresFiscalJurisdiction && !isEfrisOrMyInvois) return false;
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      return ch.name.toLowerCase().includes(q) || ch.id.includes(q);
    }
    return true;
  };

  if (isCollapsed) {
    return (
      <aside
        style={{
          width: '56px',
          backgroundColor: 'var(--theme-bg-sidebar, #0b0f19)',
          borderRight: '1px solid var(--theme-border, #1e293b)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '12px 0',
          gap: '16px',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <button onClick={onToggleCollapse} title="Expand Navigation Sidebar" style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
          <FolderOpenIcon size={18} />
        </button>
        <div style={{ fontSize: '14px', color: '#60a5fa', fontWeight: 700 }} title="Channels">#</div>
        <div title="Live Broadcasts"><BroadcastIcon size={18} color="#38bdf8" /></div>
        <div title="Users"><UsersIcon size={18} color="#34d399" /></div>
      </aside>
    );
  }

  const renderContent = () => (
    <>
      {/* Sidebar Top Header */}
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--theme-border, #1e293b)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '11px',
          fontWeight: 800,
          color: 'var(--theme-accent, #38bdf8)',
          letterSpacing: '0.05em',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FolderOpenIcon size={16} />
          <span>SOVEREIGN MESH</span>
        </div>
        {onToggleCollapse && (
          <button onClick={onToggleCollapse} title="Collapse Sidebar" style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            <FolderClosedIcon size={16} />
          </button>
        )}
      </div>

      {/* Channel Search Input */}
      <div style={{ padding: '8px 12px 4px 12px' }}>
        <input
          type="text"
          placeholder="Filter channels..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: '#090d16',
            border: '1px solid #1e293b',
            color: '#f8fafc',
            fontSize: '11px',
          }}
        />
      </div>

      {/* 1. LIVE BROADCASTS */}
      <div style={{ padding: '8px 12px 4px 12px' }}>
        <button
          onClick={() => setIsBroadcastsOpen(!isBroadcastsOpen)}
          style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', fontSize: '11px', fontWeight: 800, color: 'var(--theme-accent, #38bdf8)', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '4px 0' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {isBroadcastsOpen ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
            <span>LIVE BROADCASTS</span>
          </div>
          <span style={{ fontSize: '10px', color: '#64748b' }}>({broadcasts.length})</span>
        </button>

        {isBroadcastsOpen && (
          <div style={{ marginTop: '4px' }}>
            {broadcasts.length === 0 ? (
              <div style={{ fontSize: '11px', color: '#475569', fontStyle: 'italic', paddingLeft: '8px' }}>
                No active broadcasts
              </div>
            ) : (
              broadcasts.map((b) => (
                <div
                  key={b.uri}
                  onClick={() => onSelectBroadcast?.(b)}
                  style={{ fontSize: '12px', color: '#94a3b8', padding: '4px 8px', borderRadius: '4px', marginBottom: '2px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <BroadcastIcon size={14} color="#38bdf8" />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.topic}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div style={{ height: '1px', backgroundColor: 'var(--theme-border, #1e293b)', margin: '4px 12px' }} />

      {/* 2. CATEGORIZED CHANNELS */}
      <div style={{ padding: '4px 12px', flex: 1, overflowY: 'auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0 6px 0' }}>
          <span style={{ fontSize: '10px', fontWeight: 800, color: 'var(--theme-text-muted, #94a3b8)', letterSpacing: '0.05em' }}>
            Channels
          </span>
          {onOpenChannelModal && (
            <button
              onClick={onOpenChannelModal}
              title="Open Channels Manager"
              aria-label="Create or Manage Channels"
              style={{
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: 'rgba(56, 189, 248, 0.08)',
                border: '1px solid rgba(56, 189, 248, 0.3)',
                color: 'var(--theme-accent, #38bdf8)',
                fontSize: '10px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
              }}
            >
              <span>+ New</span>
            </button>
          )}
        </div>

        {activeCategories.map((cat) => {
          const isOpen = debouncedSearch.trim() ? true : (categoryOpenStates[cat.id] !== undefined ? !!categoryOpenStates[cat.id] : cat.defaultOpen);
          const CategoryIcon = cat.icon;
          const visibleChannels = cat.channelIds
            .map((id) => channelMap[id])
            .filter((ch) => ch && isChannelVisible(ch));

          if (debouncedSearch.trim() && visibleChannels.length === 0) {
            return null;
          }

          return (
            <div key={cat.id} style={{ marginBottom: '6px' }}>
              {/* Category Header Button */}
              <button
                type="button"
                onClick={() => toggleCategory(cat.id)}
                aria-expanded={isOpen}
                aria-controls={`category-panel-${cat.id}`}
                title={cat.description || cat.name}
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: isOpen ? 'var(--theme-text-primary, #f8fafc)' : 'var(--theme-text-muted, #94a3b8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  padding: '3px 6px',
                  borderRadius: '6px',
                  backgroundColor: isOpen ? 'rgba(255,255,255,0.03)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isOpen ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />}
                  <CategoryIcon size={13} color="var(--theme-accent, #38bdf8)" />
                  <span>{cat.name}</span>
                </div>
                <span style={{ fontSize: '10px', color: 'var(--theme-text-muted, #64748b)' }}>({visibleChannels.length})</span>
              </button>

              {/* Channels List */}
              {isOpen && (
                <div
                  id={`category-panel-${cat.id}`}
                  role="region"
                  aria-labelledby={`category-header-${cat.id}`}
                  style={{ marginTop: '2px', paddingLeft: '10px', borderLeft: '1px solid rgba(255,255,255,0.06)', marginLeft: '6px' }}
                >
                  {visibleChannels.map((channel: any) => {
                    const isKnocked = pendingKnocks.some((k) => k.roomId === channel.id);
                    const knockCount = pendingKnocks.filter((k) => k.roomId === channel.id).length;
                    const isHostInRoom = currentRoom === channel.id;
                    const isInSession = channel.memberCount && channel.memberCount > 1;

                    return (
                      <React.Fragment key={channel.id}>
                        <div
                          onClick={() => {
                          onSelectRoom(channel.id);
                          if (isOverlay) onClose?.();
                        }}
                        className={`group flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition ${
                          currentRoom === channel.id
                            ? 'bg-neutral-800 text-white font-semibold'
                            : 'text-neutral-400 hover:bg-neutral-900 hover:text-neutral-200'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 truncate">
                          {/* Dynamic Status Dot */}
                          {isKnocked ? (
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
                            </span>
                          ) : isInSession ? (
                            <span className="h-2 w-2 rounded-full bg-purple-400" title="Call In Session" />
                          ) : isHostInRoom ? (
                            <span className="h-2 w-2 rounded-full bg-emerald-400" title="Host Present" />
                          ) : (
                            <span className="h-2 w-2 rounded-full border border-neutral-600" title="Idle" />
                          )}

                          <span className="truncate">#{channel.name || channel.id}</span>

                          {/* Flashing Knock Badge with Count */}
                          {isKnocked && (
                            <span className="px-1.5 py-0.2 text-[10px] font-mono bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-full animate-pulse">
                              Knocking ({knockCount})
                            </span>
                          )}
                        </div>

                        {/* Invite Button - Bound strictly to this channel.id */}
                        {channel.id !== 'general' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setInviteRoomId(channel.id);
                              setIsInviteOpen(true);
                            }}
                            title={`Generate invite for #${channel.name || channel.id}`}
                            className="opacity-0 group-hover:opacity-100 px-2 py-0.5 text-[11px] bg-neutral-700/60 hover:bg-emerald-600 text-neutral-300 hover:text-white rounded transition"
                          >
                            + Invite
                          </button>
                        )}
                      </div>

                      {/* Connected Guests Listing directly under #private */}
                      {channel.id === 'private' && privateParticipants.length > 0 && (
                        <div className="ml-3 pl-2.5 border-l border-neutral-800 space-y-1 my-1">
                          {privateParticipants.map((p) => (
                            <div
                              key={p.id}
                              className="group/peer flex items-center justify-between px-2 py-1 rounded text-[11px] hover:bg-neutral-800/80 transition"
                            >
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelectRoom('private');
                                  if (isOverlay) onClose?.();
                                }}
                                className="flex items-center gap-1.5 min-w-0 cursor-pointer flex-1"
                                title={p.email ? `${p.name} (${p.email})` : p.name}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${p.role === 'host' ? 'bg-cyan-400' : 'bg-emerald-400'}`} />
                                <span className="text-neutral-300 truncate font-mono text-[10px]">{p.name}</span>
                                {p.role === 'host' ? (
                                  <span className="text-[9px] text-cyan-400 font-mono flex-shrink-0">(Host)</span>
                                ) : (
                                  <span className="text-[9px] text-emerald-400 font-mono flex-shrink-0">(Guest)</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {p.role !== 'host' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleKickParticipant(p.id);
                                    }}
                                    className="opacity-0 group-hover/peer:opacity-100 px-1.5 py-0.5 text-[9px] font-bold bg-red-600/80 hover:bg-red-500 text-white rounded transition"
                                    title={`Kick out ${p.name}`}
                                  >
                                    🚫 Kick
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Dynamic Plugin Slots */}
        {dynamicSlots.sidebar.length > 0 && (
          <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: '10px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
              Extension Plugins
            </div>
            {dynamicSlots.sidebar.map(({ pluginId, slot }) => {
              const channelId = slot.route ? slot.route.replace(/^\/plugins\//, '') : pluginId;
              const isActive = currentRoom === channelId;
              const label = t(`${pluginId}:${slot.titleKey}`) !== `${pluginId}:${slot.titleKey}` ? t(`${pluginId}:${slot.titleKey}`) : slot.titleKey;

              return (
                <div
                  key={`${pluginId}-${slot.route || slot.titleKey}`}
                  data-channel-id={channelId}
                  onClick={() => {
                    onSelectRoom(channelId);
                    if (isOverlay) onClose?.();
                  }}
                  style={{
                    padding: '5px 8px',
                    borderRadius: '6px',
                    marginBottom: '2px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: isActive ? 700 : 500,
                    backgroundColor: isActive ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                    color: isActive ? '#38bdf8' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <SparklesIcon size={14} color="#38bdf8" />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dynamic Micro-Frontend Sidebar Slot */}
      <div style={{ padding: '4px 12px' }}>
        <SovereignModuleSlotRenderer slot="sidebar" className="flex flex-col gap-1.5" />
      </div>

      <div style={{ height: '1px', backgroundColor: 'var(--theme-border, #1e293b)', margin: '4px 12px' }} />

      {/* 3. PEERS & USERS */}
      <div style={{ padding: '8px 12px' }}>
        <button
          onClick={() => setIsUsersOpen(!isUsersOpen)}
          style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', fontSize: '11px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '4px 0' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {isUsersOpen ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
            <span>CONNECTED PEERS</span>
          </div>
          <span style={{ fontSize: '10px', color: '#64748b' }}>({peers.length})</span>
        </button>

        {isUsersOpen && (
          <div style={{ marginTop: '4px' }}>
            {peers.length === 0 ? (
              <div style={{ fontSize: '11px', color: '#475569', fontStyle: 'italic', paddingLeft: '8px' }}>
                No active peers
              </div>
            ) : (
              peers.map((peer) => (
                <div
                  key={peer.nodeId}
                  onClick={() => onSelectPeer?.(peer.nodeId)}
                  style={{ fontSize: '12px', color: '#94a3b8', padding: '4px 8px', borderRadius: '4px', marginBottom: '2px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: peer.status === 'offline' ? '#64748b' : '#34d399' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{peer.nodeId.slice(0, 12)}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* 4. LOWER SIDEBAR UTILITIES */}
      <SidebarUtilityDock
        onOpenChannelModal={onOpenChannelModal}
        onOpenVoiceOverlay={onOpenVoiceOverlay}
        onToggleNotifications={onToggleNotifications}
        unreadNotifications={unreadNotifications}
      />
      <PrivateRoomInviteModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        roomId={inviteRoomId}
      />
    </>
  );

  // Overlay mode for mobile
  if (isOverlay) {
    return (
      <>
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, top: '48px', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 55, backdropFilter: 'blur(3px)' }}
        />
        <aside
          style={{
            position: 'fixed',
            top: '48px',
            left: 0,
            bottom: '40px',
            width: '280px',
            backgroundColor: 'var(--theme-bg-sidebar, #0b0f19)',
            borderRight: '1px solid var(--theme-border, #1e293b)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            zIndex: 56,
            boxShadow: '4px 0 24px rgba(0,0,0,0.5)',
            userSelect: 'none',
          }}
        >
          {renderContent()}
        </aside>
      </>
    );
  }

  // Desktop sidebar
  return (
    <aside
      style={{
        width: '240px',
        backgroundColor: 'var(--theme-bg-sidebar, #0b0f19)',
        borderRight: '1px solid var(--theme-border, #1e293b)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        userSelect: 'none',
        overflowY: 'auto',
      }}
    >
      {renderContent()}
    </aside>
  );
};
